import React, {
  FC,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import * as Sentry from "@sentry/react";
import { useParams } from "react-router-dom";
import Big from "big.js";
import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import { LogInFormPersonType } from "@Shape-Digital/kudzu-data/lib/types/actions";
import Box from "../../Unknown/Box";
import CircularProgress from "../../Unknown/CircularProgress";
import AppointmentCheckoutFormChoice from "../AppointmentCheckoutFormChoice";
import AppointmentCheckoutFormSection from "../AppointmentCheckoutFormSection";
import AppointmentNotPossibleDialog from "../AppointmentNotPossibleDialog";
import useAppointmentNotPossibleDialog from "../AppointmentNotPossibleDialog/useAppointmentNotPossibleDialog";
import { useAuthContext } from "../../Auth/AuthContextProvider";
import useTranslations from "./useTranslations";
import AppointmentWelcomeDialog from "../AppointmentWelcomeDialog";
import useAppointmentWelcomeDialog from "../AppointmentWelcomeDialog/useAppointmentWelcomeDialog";
import useCustomerFormData from "./useCustomerFormData";
import AppointmentCheckoutFormSectionWrapper from "../AppointmentCheckoutFormSectionWrapper";
import useCheckoutAppointment from "./useCheckoutAppointment";
import AppointmentLayout from "../AppointmentLayout";
import AppointmentCheckoutTimer from "../AppointmentCheckoutTimer";
import useAppointmentCheckoutTimer from "../AppointmentCheckoutTimer/useAppointmentCheckoutTimer";
import useDeviceType from "../../../hooks/useDeviceType";
import useBookingAddOns from "../AppointmentBookingContext/useBookingAddOns";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import { useUIContext } from "../../Unknown/UIContext";
import checkIsMinAgeValid from "./checkMinAgeRequirements";
import useSystemConfig from "../../../hooks/useSystemConfig";
import { PatientAgeAlertType } from "../../Patient/PatientAgeAlert";

const AppointmentCheckoutScreen: FC = () => {
  const hasReportedStuckSubmitButtonRef = useRef(false);
  const [isCouponApplying, setIsCouponApplying] = useState(false);
  const { appointmentId } = useParams();
  const { translations } = useTranslations();
  const { isMobile } = useDeviceType();
  const {
    buttonDesktopSubmitText,
    buttonMobileSubmitText,
    couponAlreadyApplied,
    invalidCoupon,
    defaultError,
    addOnNotFound,
  } = translations;

  const [personType, setPersonType] = useState<LogInFormPersonType | null>(
    null,
  );
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [ageAlertType, setAgeAlertType] = useState<PatientAgeAlertType | null>(
    null,
  );
  const [isValidAge, setIsValidAge] = useState(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<DatabaseRow<"customers"> | null>(null);

  const { setAlert } = useUIContext();

  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);

  const systemConfig = useSystemConfig();

  const applyAppointmentCoupon = useFirebaseAppFunction(
    "applyAppointmentCoupon",
  );

  const { signIn, isAuthenticated, answerCustomChallenge } = useAuthContext();

  const { data: customerFormData, status: customerFormDataStatus } =
    useCustomerFormData();

  const {
    data: appointmentData,
    status: appointmentStatus,
    refetch,
  } = useCheckoutAppointment(appointmentId);

  const {
    isNotPossibleDialogOpen,
    notPossibleQuestion,
    onNotPossibleAnswerClick,
    onNotPossibleClose,
  } = useAppointmentNotPossibleDialog();

  const onCouponApply = async () => {
    setIsCouponApplying(true);
    try {
      if (!couponCodeInput) {
        setCouponError(invalidCoupon);
        return;
      }

      const response = await applyAppointmentCoupon({
        couponCode: couponCodeInput,
        appointmentId,
      });

      if (response.data.status === "error") {
        const { code } = response.data.error;
        switch (code) {
          case "COUPON_ALREADY_APPLIED": {
            setCouponError(couponAlreadyApplied);
            return;
          }
          case "ADD_ON_NOT_FOUND": {
            setCouponError(addOnNotFound);
            return;
          }
          case "INVALID_COUPON": {
            setCouponError(invalidCoupon);
            return;
          }
          default: {
            throw new Error(defaultError);
          }
        }
      }

      if (response.data.status === "success") {
        refetch();
        setCouponCodeInput("");
        setCouponError(null);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : defaultError;
      setAlert({ severity: "error", message, isShown: true });
    } finally {
      setIsCouponApplying(false);
    }
  };

  const {
    onWelcomeOpen,
    welcomeEmail,
    isWelcomeDialogOpen,
    onWelcomeResendButtonClick,
    onWelcomeClose,
    formik,
  } = useAppointmentWelcomeDialog({ appointmentId, answerCustomChallenge });

  const appointmentCheckoutTimerProps = useAppointmentCheckoutTimer({
    appointmentId,
    appointmentData: appointmentData || null,
  });

  const { isCenterUser, isCenterUserLoading } = useAuthContext();

  const { data: centerAddOns, status: addOnsStatus } = useBookingAddOns(
    appointmentData?.center?.id,
    isCenterUser,
  );

  const onEmailBlur = useCallback(
    async (email: string, valid: boolean) => {
      if (!email || !valid) {
        return;
      }

      if (isAuthenticated) {
        const currentEmail = customerFormData?.customer.email;
        if (!currentEmail || email === currentEmail) return;
        return;
      }

      try {
        setIsLoginLoading(true);

        const isSent = await signIn({ email });
        setIsCodeSent(isSent);
        if (isSent) onWelcomeOpen(email);
      } catch (error) {
        // eslint-disable-next-line no-console, no-alert
        alert(translations.emailCanNotBeUsed);
      } finally {
        setIsLoginLoading(false);
      }
    },
    [
      isAuthenticated,
      customerFormData,
      onWelcomeOpen,
      signIn,
      translations.emailCanNotBeUsed,
    ],
  );

  const onDateBirthBlur = useCallback(
    (dateBirth: string) => {
      if (!systemConfig.data || !appointmentData) {
        return;
      }

      const ageValidationResult = checkIsMinAgeValid({
        dateBirth,
        minOnlineBookingAge: systemConfig.data.minOnlineBookingAge,
        minSelfBookingAge: appointmentData.center.minSelfBookingAge,
        isLegalGuardian:
          personType === "legalGuardian" || personType === "familyMember",
        isCenterUser,
      });

      if (!ageValidationResult.isValid && ageValidationResult.errorType) {
        setAgeAlertType(ageValidationResult.errorType);

        return;
      }

      setIsValidAge(true);
      setAgeAlertType(null);
    },
    [appointmentData, isCenterUser, personType, systemConfig.data],
  );

  const onDateBirthChange = useCallback(
    (dateBirth: string) => {
      if (!systemConfig.data || !appointmentData) {
        return;
      }

      const ageValidationResult = checkIsMinAgeValid({
        dateBirth,
        minOnlineBookingAge: systemConfig.data.minOnlineBookingAge,
        minSelfBookingAge: appointmentData.center.minSelfBookingAge,
        isLegalGuardian:
          personType === "legalGuardian" || personType === "familyMember",
        isCenterUser,
      });

      if (ageValidationResult.isValid) {
        setAgeAlertType(null);
        setIsValidAge(true);
      }

      if (!ageValidationResult.isValid) {
        setIsValidAge(false);
      }
    },
    [appointmentData, isCenterUser, personType, systemConfig.data],
  );

  const priceWithDiscount = useMemo(() => {
    if (appointmentStatus !== "success") return 0;
    const { totalCostAmount, discountAmount } = appointmentData;

    return Big(totalCostAmount).minus(discountAmount).toNumber();
  }, [appointmentData, appointmentStatus]);

  const discountSum = useMemo(() => {
    if (appointmentStatus !== "success") return 0;
    const { couponAmount, discountAmount } = appointmentData;

    return Big(discountAmount || 0)
      .minus(couponAmount || 0)
      .toNumber();
  }, [appointmentData, appointmentStatus]);

  const onResendClick = useCallback(async () => {
    try {
      setIsLoginLoading(true);
      onWelcomeResendButtonClick();

      const isSent = await signIn({ email: welcomeEmail });
      setIsCodeSent(isSent);
      if (isSent) onWelcomeOpen(welcomeEmail);
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert(translations.emailCanNotBeUsed);
    } finally {
      setIsLoginLoading(false);
    }
  }, [
    onWelcomeOpen,
    onWelcomeResendButtonClick,
    signIn,
    translations.emailCanNotBeUsed,
    welcomeEmail,
  ]);

  useEffect(() => {
    if (appointmentData && !isInitialized) {
      if (appointmentData?.appointmentCustomerDetails?.length) {
        const customerDetails = appointmentData.appointmentCustomerDetails[0];
        const typeMap: Partial<Record<string, LogInFormPersonType>> = {
          patient: "patient",
          legal_guardian: "legalGuardian",
          family_member: "familyMember",
          other_person: "otherPerson",
        };

        const mapped = typeMap[customerDetails?.type];

        if (mapped) setPersonType(mapped);
      }

      setIsInitialized(true);
    }
  }, [
    appointmentData,
    appointmentData?.appointmentCustomerDetails,
    isInitialized,
  ]);

  const onSelectCustomer = useCallback(
    (customer: DatabaseRow<"customers">) => {
      if (!systemConfig?.data || !appointmentData) {
        return;
      }
      setSelectedCustomer(customer);

      // Selecting a customer from search is equivalent to the customer logging in.
      // If the center user was in otherPerson mode, transition to patient so that
      // LinkedPatientForm is shown (same as the authenticated patient path).
      const effectivePersonType =
        personType === "otherPerson" ? "patient" : personType;

      if (personType === "otherPerson") {
        setPersonType("patient");
      }

      if (effectivePersonType === "patient") {
        const validationResult = checkIsMinAgeValid({
          dateBirth: customer.date_birth,
          minOnlineBookingAge: systemConfig.data.minOnlineBookingAge,
          minSelfBookingAge: appointmentData.center.minSelfBookingAge,
          isLegalGuardian: false,
          isCenterUser,
        });

        setIsValidAge(validationResult.isValid);
        if (!validationResult.isValid && validationResult.errorType) {
          setAgeAlertType(validationResult.errorType);
        }
      }
    },
    [appointmentData, isCenterUser, personType, systemConfig.data],
  );

  // Initialize age validation from pre-filled patient DOB (e.g. returning customer with saved cart).
  // The linked-patient flow handles this itself via its own useEffect.
  useEffect(() => {
    if (!isInitialized || !personType) return;
    const isLinkedFlow =
      personType !== null &&
      personType !== "otherPerson" &&
      (!!customerFormData || !!selectedCustomer);
    if (isLinkedFlow) return;

    const patientDob =
      appointmentData?.appointmentPatientDetails?.[0]?.date_birth ||
      (personType === "patient" ? customerFormData?.patient?.date_birth : null);
    if (!patientDob) return;

    onDateBirthBlur(patientDob);
  }, [
    isInitialized,
    personType,
    onDateBirthBlur,
    appointmentData,
    customerFormData,
    selectedCustomer,
  ]);

  if (!appointmentId) {
    return null;
  }

  if (
    (appointmentStatus !== "success" && !appointmentData) ||
    customerFormDataStatus !== "success" ||
    !appointmentCheckoutTimerProps ||
    isCenterUserLoading ||
    addOnsStatus !== "success" ||
    !appointmentData ||
    !isInitialized ||
    systemConfig.status !== "success"
  ) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexGrow={1}
        mt={10}
      >
        <CircularProgress />
      </Box>
    );
  }

  const {
    center,
    offerings,
    packages,
    addOns,
    timeSlot,
    depositAmount,
    couponAmount,
    couponCode,
  } = appointmentData;

  const confirmAndPayText = isMobile
    ? buttonMobileSubmitText
    : buttonDesktopSubmitText;

  const isPaymentRequired = priceWithDiscount > 0 && depositAmount > 0;

  const buttonSubmitText = isPaymentRequired
    ? confirmAndPayText
    : translations.saveAppointment;

  return (
    <AppointmentCheckoutFormSectionWrapper
      personType={personType}
      customerFormData={customerFormData}
      selectedCustomer={selectedCustomer}
      isPaymentRequired={isPaymentRequired}
      customerDetails={appointmentData.appointmentCustomerDetails?.[0]}
      patientDetails={appointmentData.appointmentPatientDetails?.[0]}
    >
      {({ isValid, dirty, resetForm, handleSubmit, isSubmitting }) => {
        const isEmailInUse = isCodeSent && !customerFormData?.customer.email;

        const isLoggedIn =
          !!customerFormData?.customer.email || !!selectedCustomer;
        const isSubmitButtonDisabled =
          !isValidAge ||
          isEmailInUse ||
          personType === null ||
          !isValid ||
          (!isLoggedIn && !dirty) ||
          isCouponApplying ||
          isSubmitting;

        // Diagnostic: every OTHER reason the submit button can be disabled
        // has a visible on-screen explanation (email-verification code
        // input, unselected person type, coupon spinner, an age alert). The
        // one exception is `!isValidAge` with no `ageAlertType` set — the
        // age check simply never resolved, so the button is disabled with
        // no indication why. Report only that specific, unexplained case,
        // once per page load, so the next occurrence is diagnosable instead
        // of guessed at from an unreproducible customer report.
        const isAgeCheckSilentlyStuck = !isValidAge && !ageAlertType;

        if (
          isValid &&
          (isLoggedIn || dirty) &&
          isAgeCheckSilentlyStuck &&
          !hasReportedStuckSubmitButtonRef.current
        ) {
          hasReportedStuckSubmitButtonRef.current = true;

          let dateOfBirthSource = "none";
          if (customerFormData?.patient?.date_birth) {
            dateOfBirthSource = "customerFormData";
          } else if (
            appointmentData?.appointmentPatientDetails?.[0]?.date_birth
          ) {
            dateOfBirthSource = "appointmentData";
          }

          Sentry.captureMessage(
            "Checkout confirm-and-pay button silently disabled: age check never resolved",
            {
              level: "warning",
              tags: { appointmentId: appointmentId ?? "unknown" },
              extra: {
                isLoggedIn,
                dirty,
                personType,
                dateOfBirthSource,
              },
            },
          );
        }

        const onPersonTypeChange = (type: LogInFormPersonType) => {
          resetForm();
          setPersonType(type);
          setIsValidAge(false);
          setAgeAlertType(null);
        };

        return (
          <AppointmentLayout
            selectedCenter={center}
            selectedDateTime={timeSlot.startedAt}
            selectedCenterServices={[...offerings, ...packages]}
            selectedAddOns={addOns}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
            isSubmitLoading={isSubmitting}
            buttonSubmitText={buttonSubmitText}
            depositAmount={depositAmount}
            discountSum={discountSum}
            centerAddOns={centerAddOns || []}
            priceWithDiscount={priceWithDiscount}
            isCouponDisabled={isCouponApplying || isSubmitting}
            couponCodeInput={couponCodeInput}
            couponError={couponError}
            couponAmount={couponAmount}
            couponCode={couponCode}
            setCouponError={setCouponError}
            onSubmitClick={handleSubmit}
            onCouponApply={onCouponApply}
            setCouponCodeInput={setCouponCodeInput}
          >
            <AppointmentCheckoutTimer {...appointmentCheckoutTimerProps} />
            {!customerFormData && !selectedCustomer && (
              <AppointmentCheckoutFormChoice
                personType={personType}
                setPersonType={onPersonTypeChange}
                isDisabled={isLoginLoading}
              />
            )}
            <Box mt={8}>
              <AppointmentCheckoutFormSection
                personType={personType}
                onPersonTypeChange={setPersonType}
                onNotPossibleAnswerClick={onNotPossibleAnswerClick}
                isEmailLoading={isLoginLoading}
                isEmailInUse={isEmailInUse}
                isCheckDataShown={!!customerFormData}
                onEmailBlur={isCenterUser ? undefined : onEmailBlur}
                isCenterUser={isCenterUser}
                selectedCustomer={selectedCustomer}
                prefilledPatient={customerFormData?.patient}
                onSelectCustomer={onSelectCustomer}
                centerName={appointmentData.center.name}
                centerPhone={appointmentData.center.phoneNumber}
                minOnlineBookingAge={systemConfig.data.minOnlineBookingAge}
                minSelfBookingAge={appointmentData.center.minSelfBookingAge}
                ageAlertType={ageAlertType}
                onDateBirthChange={onDateBirthChange}
                onDateBirthBlur={onDateBirthBlur}
              />
            </Box>
            <AppointmentNotPossibleDialog
              question={notPossibleQuestion}
              centerName={center.name}
              phoneNumber={center.phoneNumber}
              isOpen={isNotPossibleDialogOpen}
              onClose={onNotPossibleClose}
            />
            <AppointmentWelcomeDialog
              email={welcomeEmail}
              isOpen={isWelcomeDialogOpen}
              onResendClick={onResendClick}
              onClose={onWelcomeClose}
              formik={formik}
            />
          </AppointmentLayout>
        );
      }}
    </AppointmentCheckoutFormSectionWrapper>
  );
};

export default AppointmentCheckoutScreen;
